import {Message} from 'extensions/messages';

/* eslint-disable max-lines-per-function */
export class PrintableController {

	/**
	 * @param {import('lit').LitElement} host
	 */
	constructor (host) {
		(this.host = host).addController(this);
	}

	/**
	 * @param {string} selector
	 * @returns {HTMLElement}
	 */
	_getPrintableElement (selector) {
		return this.host?.renderRoot?.querySelector(selector);
	}

	/**
	 * Print a specific selector contained in the renderRoot of the host element.
	 * Uses html2canvas to generate an image of the selected element.
	 * @see https://html2canvas.hertzen.com/
	 * @param {string} selector
	 * @returns {Promise<void>}
	 */
	async print (selector) {
		const element = this._getPrintableElement(selector);

		if (element) {
			let success = false;

			// Set loading state to show a loader in the host element
			this.host.loading = true;

			// Load script dynamically to prevent bloating the bundle
			const {default: html2canvas} = await import('html2canvas');

			// Prevent cropping of canvas element by removing the scrollbar of the element
			element.style.height = 'auto';
			element.style.overflow = 'visible';

			// Set timeout to remove loading animation; log/show error if necessary
			setTimeout(() => {
				this.host.loading = false;
				if (!success && this.host?.messageContainer) {
					const errorMessage = 'Unable to print content. Please try again.';

					this.host.messageContainer.add(new Message(errorMessage, {
						autohide: true,
						type: 'error'
					}));

					// eslint-disable-next-line no-console
					console.error('PrintController ::', errorMessage);
				}
			}, 10000);

			// Create a <canvas> tag from the raw html output (used to generate an image)
			const canvas = await html2canvas(element, {
				allowTaint: true,
				scrollX: -window.scrollX,
				scrollY: -window.scrollY,
				useCORS: true
			});

			success = true;

			// Reset styles after the canvas is created
			element.style.height = '';
			element.style.overflow = '';

			// Convert canvas into an image data:url; create tag for window injection
			const imageUrl = canvas.toDataURL('image/png');
			const image = document.createElement('img');
			image.src = imageUrl;

			/**
			 * Create inline style tag to fix a few styling issues
			 * @see https://stackoverflow.com/questions/7846346/how-to-avoid-extra-blank-page-at-end-while-printing
			 */
			const inlineStyles = document.createElement('style');
			inlineStyles.innerHTML = `
				@media print {
					@page {
						margin: 2.5%;
						orphans: 2;
						size: letter;
						widows: 2;
					}

					html, body {
						height: 99%;
					}
				}

				img {
					max-width: 100%;
				}
			`;

			// Stop loading animation
			this.host.loading = false;

			// Generate new tab/window to inject the canvas image into
			const printWindow = window.open('', 'Print Window');

			// Close print window when the print dialog closes
			printWindow.onafterprint = () => {
				printWindow.close();
			};

			// Append genrate image/styles into new window
			printWindow.document.head.appendChild(inlineStyles);
			printWindow.document.body.appendChild(image);

			// Focus and fire print method after image loads
			printWindow.focus();
			image.onload = () => {
				printWindow.print();
			};
		}
	}
};
